import MetadataValidationModal from '@/components/MetadataValidationModal/MetadataValidationModal.vue';
import MissingMainFilesModal from '@/components/MissingMainFilesModal/MissingMainFilesModal.vue';
import { compareFileNames, parseDateToUsFormat } from '@/helpers';
import { groupBy } from 'lodash';
import {
  AssignmentType,
  ContentTypeFieldType,
  IFieldMapping,
  IFileMetadata,
} from 'types';
import {
  alphaNumericDashUnderscoreRegex,
  generateDocumentId,
  getExtensionFromFilepath,
} from 'utils';
import { Ref, ref } from 'vue';
import { IUploadRecord } from '../MassUpload.types';

export function useMassUploadRecordsHandler(
  isNoMetadataUploadMode: Ref<boolean>,
  uploadRecords: Ref<IUploadRecord[]>,
  contentTypeMapping: Ref<IFieldMapping[]>,
) {
  /**
   * Is CSV missing fields that are needed according
   * to the saved content type mapping
   */
  const isWrongCsvError = ref(false);
  const columnsWithMissingValues = ref<Set<string>>(new Set());
  /**
   * Does the CSV contain more than two files (pdf + rendition) for the same
   * `documentId` or two files for one `documentId` that are both PDFs
   */
  const isDocIdsWithToManyFilesError = ref(false);
  const docIdsWithToManyFiles = ref<Set<string>>(new Set());

  /**
   * Does the CSV contain two files for one `documentId` where none of them is
   * a main (PDF) file.
   */
  const isMissingMainFileError = ref(false);
  const docIdsWithMissingMainFile = ref<Set<string>>(new Set());

  /**
   * `documentId`s that contain invalid characters
   */
  const invalidDocumentIds = ref<string[]>([]);

  /**
   * Does the CSV contain `documentId`s that have only rendition files
   * (i.e. no main PDF file).
   */
  const isOnlyRenditionFileWarning = ref(false);
  const docIdsWithOnlyRenditionFile = ref<Set<string>>(new Set());

  function addMassUploadRecords(records: IUploadRecord[]) {
    const mappedRecords: IUploadRecord[] = [];
    records.forEach((record) => {
      /**
       * When uploding files without metadata, `documentId` needs to be
       * generated automatically. The original file name is used as `Filepath`
       * since there is no filepath from the CSV and it is later needed for
       * checking the amount of files per `documentId`.
       */
      if (isNoMetadataUploadMode.value) {
        record.metadata ??= {} as IFileMetadata;
        record.metadata.documentId = getDocumentId(record, mappedRecords);
        record.metadata.Filepath = record.file!.name;
        return mappedRecords.push(record);
      }
      const recordWithMappedMetadata = mapMetadataForUploadRecord(record);
      if (isEmptyCsvRow(recordWithMappedMetadata)) return;
      /**
       * In digitalization projects the content type field mapping for
       * `documentId` can be missing. This is a different case then when
       * `isNoMetadataUploadMode` is `true`
       */
      if (!recordWithMappedMetadata.metadata?.documentId) {
        recordWithMappedMetadata.metadata!.documentId = generateDocumentId();
      }
      mappedRecords.push(recordWithMappedMetadata);
    });

    if (!validateFilesByDocIds(mappedRecords)) return;
    /**
     * Matching rendition files after ensuring correct files by `documentId`
     * structure.
     */
    const matchedRecords = matchRenditionFiles(mappedRecords);

    if (!isNoMetadataUploadMode.value) {
      if (!validateCsvColumns(matchedRecords)) return;
    }
    if (!validateDocumentIds(matchedRecords)) return;

    /**
     * Showing a warning modal if there are `documentId`s that have only
     * rendition files.
     */
    showModalIfOnlyRenditionWarning();

    uploadRecords.value.push(...matchedRecords);
  }

  /**
   * Assigning values to content type fields according to configured
   * content type mapping
   */
  function mapMetadataForUploadRecord(record: IUploadRecord): IUploadRecord {
    if (!contentTypeMapping.value) {
      throw new Error('Fields mapping is not available');
    }
    const mappedMetadata: IFileMetadata = {} as IFileMetadata;
    contentTypeMapping.value.forEach((mapping) => {
      mappedMetadata[mapping.field] = mapMetadataValue(
        mapping,
        record.rawMetadata[mapping.value],
      );
    });
    record.metadata = mappedMetadata;
    return record;
  }

  function mapMetadataValue(
    mapping: IFieldMapping,
    valueFromCsv?: string,
  ): string | Date | undefined {
    if (mapping.assignmentType === AssignmentType.csvField) {
      if (!valueFromCsv) return;
      if (mapping.dataType === ContentTypeFieldType.DATE_TIME) {
        /**
         * Formatting date values from CSV to en-US format accepted by
         * Sharepoint
         */
        return parseDateToUsFormat(valueFromCsv);
      } else {
        return valueFromCsv;
      }
    } else if (
      /**
       * Assigning constant values
       */
      mapping.assignmentType === AssignmentType.constantAssignment
    ) {
      return mapping.value;
    } else if (mapping.assignmentType === AssignmentType.currentDate) {
      /**
       * Mapping current dates in en-US format accepted by Sharepoint
       */
      return new Date().toLocaleDateString('en-US');
    }
  }

  /**
   * In no-metadata upload mode, rendition files are matched with main files by
   * their original file names without extensions. Generating same document Ids
   * for these files makes it possible to match them using the same logic as
   * for files with metadata.
   */
  function getDocumentId(
    record: IUploadRecord,
    mappedRecords: IUploadRecord[],
  ): string {
    if (!isNoMetadataUploadMode.value) return generateDocumentId();
    const matchingFile = mappedRecords.find(
      (mappedRecord) =>
        mappedRecord.file?.name &&
        record.file?.name &&
        compareFileNames(mappedRecord.file.name, record.file.name),
    );
    /** If a matching file is found, use its documentId */
    return matchingFile?.metadata?.documentId ?? generateDocumentId();
  }

  /**
   * Matching rendition files with main PDF files by their `documentId`
   */
  function matchRenditionFiles(records: IUploadRecord[]): IUploadRecord[] {
    const matchedRecords: IUploadRecord[] = [];
    const groupedByDocId = groupBy(
      records,
      (record) => record.metadata?.documentId,
    );
    Object.entries(groupedByDocId).forEach(([, records]) => {
      if (records.length === 2) {
        const pdfFile = records.find(
          (record) =>
            record.metadata?.Filepath &&
            getExtensionFromFilepath(record.metadata.Filepath) === 'pdf',
        );
        const renditionFile = records.find(
          (record) =>
            record.metadata?.Filepath &&
            getExtensionFromFilepath(record.metadata.Filepath) !== 'pdf',
        );
        pdfFile!.renditionFile = {
          file: renditionFile?.file,
          metadata: renditionFile?.metadata,
        };
        matchedRecords.push(pdfFile!);
      } else matchedRecords.push(...records);
    });
    return matchedRecords;
  }

  const metadataValidationModalRef = ref<InstanceType<
    typeof MetadataValidationModal
  > | null>(null);
  function validateDocumentIds(records: IUploadRecord[]): boolean {
    const invalidRecords = records.filter((record) => {
      const documentId = record.metadata!.documentId;
      return !alphaNumericDashUnderscoreRegex.test(documentId);
    });
    invalidDocumentIds.value = invalidRecords.map(
      (record) => record.metadata!.documentId,
    );
    const hasInvalidDocumentIds = !!invalidDocumentIds.value.length;
    if (hasInvalidDocumentIds) {
      metadataValidationModalRef.value?.show();
    }
    return !hasInvalidDocumentIds;
  }

  function validateFilesByDocIds(records: IUploadRecord[]): boolean {
    docIdsWithToManyFiles.value.clear();
    docIdsWithMissingMainFile.value.clear();
    docIdsWithOnlyRenditionFile.value.clear();
    const groupedByDocId = groupBy(
      records,
      (record) => record.metadata?.documentId,
    );
    Object.entries(groupedByDocId).forEach(([documentId, records]) => {
      /**
       * If there are two files for the same document ID, one of them has to be
       * a PDF file and the other one has to be a rendition file with a
       * different extension.
       */
      if (
        records.length === 2 &&
        records.every(
          (r) => getExtensionFromFilepath(r.metadata!.Filepath) === 'pdf',
        )
      ) {
        docIdsWithToManyFiles.value.add(documentId);
      } else if (
        records.length === 2 &&
        !records.some(
          (r) => getExtensionFromFilepath(r.metadata!.Filepath) === 'pdf',
        )
      ) {
        docIdsWithMissingMainFile.value.add(documentId);
      } else if (records.length > 2) {
        docIdsWithToManyFiles.value.add(documentId);
      } else if (
        records.length === 1 &&
        getExtensionFromFilepath(records[0].metadata!.Filepath) !== 'pdf'
      ) {
        docIdsWithOnlyRenditionFile.value.add(documentId);
      }
    });
    isDocIdsWithToManyFilesError.value = !!docIdsWithToManyFiles.value.size;
    isMissingMainFileError.value = !!docIdsWithMissingMainFile.value.size;
    isOnlyRenditionFileWarning.value = !!docIdsWithOnlyRenditionFile.value.size;
    return !isDocIdsWithToManyFilesError.value && !isMissingMainFileError.value;
  }

  function validateCsvColumns(records: IUploadRecord[]): boolean {
    if (!contentTypeMapping.value) {
      throw new Error('Content type fields mapping not available');
    }
    isWrongCsvError.value = false;
    columnsWithMissingValues.value.clear();
    /**
     * Checking if some record does not miss a field that is required and
     * should be mapped according to the saved CSV mapping
     */
    const requiredCsvFields = contentTypeMapping.value!.filter(
      (mapping) =>
        mapping.assignmentType === AssignmentType.csvField && mapping.required,
    );
    records.forEach((record) => {
      requiredCsvFields
        .filter((field) => !record.rawMetadata[field.value])
        .map((field) => field.value)
        .forEach((field) => columnsWithMissingValues.value.add(field));
    });
    isWrongCsvError.value = !!columnsWithMissingValues.value.size;
    return !isWrongCsvError.value;
  }

  /**
   * @param uploadRecord - record from the CSV file with mapped metadata
   */
  function isEmptyCsvRow(uploadRecord: IUploadRecord): boolean {
    return (
      !Object.values(uploadRecord.metadata ?? {}).length ||
      /**
       * If the filepath is not present, than this record is empty (probably
       * empty row from CSV) and should be ignored.
       */
      !uploadRecord.metadata?.Filepath
    );
  }

  const missingMainFilesModalRef = ref<InstanceType<
    typeof MissingMainFilesModal
  > | null>(null);
  function showModalIfOnlyRenditionWarning(): void {
    if (isOnlyRenditionFileWarning.value) {
      missingMainFilesModalRef.value?.show();
    }
  }

  return {
    addMassUploadRecords,
    isWrongCsvError,
    columnsWithMissingValues,
    isDocIdsWithToManyFilesError,
    docIdsWithToManyFiles,
    isMissingMainFileError,
    docIdsWithMissingMainFile,
    docIdsWithOnlyRenditionFile,
    missingMainFilesModalRef,
    invalidDocumentIds,
    metadataValidationModalRef,
  };
}
